import React, { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { api, describeApiError } from "../lib/api";
import { trackEvent } from "../lib/analytics";

export const LeadMagnetModal = ({ open, onClose }) => {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // Lock body scroll while modal is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Reset state every time the modal closes
  useEffect(() => {
    if (open) return;
    const id = setTimeout(() => {
      setForm({ name: "", email: "", company: "" });
      setConsent(false);
      setSubmitting(false);
      setSuccess(false);
      setError("");
    }, 250);
    return () => clearTimeout(id);
  }, [open]);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return;
    setSubmitting(true);
    setError("");
    try {
      await api.post("/leads/esg-report", {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        consent: true,
        lang,
      });
      trackEvent("lead_esg_report_submitted", {
        event_category: "engagement",
        event_label: "ESG Report download",
      });
      setSuccess(true);
    } catch (err) {
      setError(describeApiError(err, t.lead.form_error));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="lead-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[70] flex items-center justify-center px-4 py-8 bg-[#0D2F4C]/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg bg-white shadow-2xl"
        style={{ borderRadius: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="lead-modal-close-btn"
          onClick={onClose}
          aria-label={t.lead.close}
          className="absolute top-4 right-4 text-[#0D2F4C]/60 hover:text-[#0D2F4C] transition-colors p-2"
        >
          <X size={20} />
        </button>

        {!success ? (
          <form onSubmit={submit} className="p-8 sm:p-10">
            <div className="text-xs uppercase tracking-[0.25em] text-[#C9A961] mb-2">
              {t.lead.overline}
            </div>
            <h3
              className="text-2xl sm:text-3xl text-[#0D2F4C] mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t.lead.modal_title}
            </h3>
            <p className="text-sm text-[#0D2F4C]/70 mb-7 leading-relaxed">
              {t.lead.modal_subtitle}
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#0D2F4C]/60 mb-1.5">
                  {t.lead.form_name}
                </label>
                <input
                  data-testid="lead-form-name"
                  name="name"
                  type="text"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={handleChange}
                  className="w-full bg-white text-[#0D2F4C] border border-[#0D2F4C]/20 px-4 py-2.5 text-sm focus:outline-none focus:border-[#0D2F4C] transition-colors"
                  style={{ borderRadius: 0 }}
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#0D2F4C]/60 mb-1.5">
                  {t.lead.form_email}
                </label>
                <input
                  data-testid="lead-form-email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  className="w-full bg-white text-[#0D2F4C] border border-[#0D2F4C]/20 px-4 py-2.5 text-sm focus:outline-none focus:border-[#0D2F4C] transition-colors"
                  style={{ borderRadius: 0 }}
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#0D2F4C]/60 mb-1.5">
                  {t.lead.form_company}
                </label>
                <input
                  data-testid="lead-form-company"
                  name="company"
                  type="text"
                  value={form.company}
                  onChange={handleChange}
                  className="w-full bg-white text-[#0D2F4C] border border-[#0D2F4C]/20 px-4 py-2.5 text-sm focus:outline-none focus:border-[#0D2F4C] transition-colors"
                  style={{ borderRadius: 0 }}
                />
              </div>
              <label className="flex items-start gap-3 text-xs text-[#0D2F4C]/75 leading-relaxed cursor-pointer">
                <input
                  data-testid="lead-form-consent"
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 accent-[#0D2F4C]"
                />
                <span>{t.lead.form_consent}</span>
              </label>
            </div>

            {error && (
              <div
                data-testid="lead-form-error"
                className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-2"
              >
                {error}
              </div>
            )}

            <button
              data-testid="lead-form-submit"
              type="submit"
              disabled={!consent || submitting}
              className="mt-6 w-full bg-[#0D2F4C] text-white px-6 py-3 text-sm tracking-wide hover:bg-[#0A243A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ borderRadius: 0 }}
            >
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {submitting ? t.lead.form_submitting : t.lead.form_submit}
            </button>
          </form>
        ) : (
          <div
            data-testid="lead-form-success"
            className="p-8 sm:p-10 text-center"
          >
            <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-[#C9A961]/15 flex items-center justify-center">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#C9A961"
                strokeWidth="2"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <h3
              className="text-2xl text-[#0D2F4C] mb-3"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {lang === "fr" ? "Merci !" : "Thank you!"}
            </h3>
            <p className="text-sm text-[#0D2F4C]/70 leading-relaxed mb-6">
              {t.lead.form_success}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                data-testid="lead-success-download-btn"
                href={`/reports/esg-africa-2026-${lang}.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#C9A961] text-[#0D2F4C] px-6 py-2.5 text-sm tracking-wide hover:bg-[#D4B775] transition-colors font-medium inline-flex items-center justify-center gap-2"
                style={{ borderRadius: 0 }}
              >
                {t.lead.download_now}
              </a>
              <button
                data-testid="lead-success-close-btn"
                onClick={onClose}
                className="bg-transparent border border-[#0D2F4C]/30 text-[#0D2F4C] px-6 py-2.5 text-sm tracking-wide hover:bg-[#0D2F4C]/5 transition-colors"
                style={{ borderRadius: 0 }}
              >
                {t.lead.close}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
